import { ConfigurableTable } from 'src/common/components/ConfigurableTable/ConfigurableTable';

import { TextCell } from 'src/common/components/ConfigurableTable/Components/Cells/TextCell';
import { useAtom } from 'jotai';
import { Suspense, useCallback } from 'react';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { createPortal } from 'react-dom';

import { Sorting } from 'src/common/components/Filters/constants/sorting';
import { useProductAccountRef } from './hooks/useProductAccountRef';
import { ProductSummaryLastUpdate } from './components/ProductSummaryLastUpdate';
import { ProductSummaryNoRows } from './components/ProductSummaryNoRows';
import { useProductSummary } from './hooks/useProductSummary';
import { useProductSummaryColumns } from './hooks/useProductSummaryColumns';
import { ProductSummaryColumnName } from '../constants/productSummaryConstant';
import {
  productSummaryPageFilterAtom,
  productSummaryRowsPerPageFilterAtom,
  productSummarySortingAtomWithStorage,
} from 'src/atoms/filters/product-summary';
import { useSelectedProduct } from '../hooks/useSelectedProduct';
import { TABS_ROOT_ID } from 'src/common/components/AppLayout/Components/TabsContainer';

export const ProductSummaryInventory = () => {
  const [rowsPerPage, setRowsPerPage] = useAtom(productSummaryRowsPerPageFilterAtom);
  const [sorting, setSorting] = useAtom(productSummarySortingAtomWithStorage);
  const [page, setPage] = useAtom(productSummaryPageFilterAtom);

  const accountRef = useProductAccountRef();
  const { setSelectedProductId } = useSelectedProduct();

  const columnConfiguration = useProductSummaryColumns();
  const { data: tariffSummary } = useProductSummary({
    accountRef: accountRef,
  });

  const onPaginationChange = useCallback(
    (pagination: GridPaginationModel) => {
      if (pagination.page !== page) {
        setPage(pagination.page);
      }

      if (pagination.pageSize !== rowsPerPage) {
        setRowsPerPage(pagination.pageSize);
      }
    },
    [page, rowsPerPage, setPage, setRowsPerPage],
  );

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const [sortField] = model;
    if (!sortField) {
      return;
    }

    setSorting({
      columnName: sortField.field as ProductSummaryColumnName,
      sort: sortField.sort as Sorting,
    });
  }, []);

  const tabsRoot = document.getElementById(TABS_ROOT_ID);

  return (
    <>
      {tabsRoot &&
        createPortal(
          <Suspense fallback={''}>
            <ProductSummaryLastUpdate />
          </Suspense>,
          tabsRoot,
        )}
      <ConfigurableTable
        getRowId={(row) => row.bundleProductCode}
        configuration={columnConfiguration}
        rows={tariffSummary.items}
        page={page}
        pageSize={rowsPerPage}
        onPaginationChange={onPaginationChange}
        totalNumberOfItems={tariffSummary.totalNumberOfItems}
        cellConfiguration={{
          default: TextCell,
        }}
        slots={{
          noRowsOverlay: ProductSummaryNoRows,
        }}
        sort={sorting}
        onSortModelChange={onSortModelChange}
        disableRowSelectionOnClick={false}
        onRowSelect={(productId) => setSelectedProductId(productId)}
      />
    </>
  );
};
